/* Contrats d'énergie d'un site : prélèvement (énergie achetée) et injection (énergie vendue).
 * Prix fixe ou formule variable (A × X + B), chacun monohoraire ou bihoraire (heures pleines / heures creuses). */

export type ContractKind = 'offtake' | 'injection';

interface ContractColumns {
  supplier: string; type: string; tariff: string;
  /** Prix fixe monohoraire (bihoraire : copie du prix heures pleines, comme le formulaire admin) */
  fixed: string; hp: string; hc: string;
  /** Formule monohoraire A × X + B ; bihoraire : A1 × X + B1 (heures pleines), A2 × X + B2 (heures creuses) */
  x: string; a: string; b: string; a1: string; b1: string; a2: string; b2: string;
  start: string; end: string;
}

/** Colonnes de la fiche participant pour chaque contrat (prélèvement / injection). */
export const CONTRACT_FIELDS: Record<ContractKind, ContractColumns> = {
  offtake: {
    supplier: 'supplier_name', type: 'contract_formula_type', tariff: 'tariff_type',
    fixed: 'commodity_rate', hp: 'commodity_rate_hp', hc: 'commodity_rate_hc',
    x: 'formula_x_param', a: 'formula_a', b: 'formula_b', a1: 'formula_a1', b1: 'formula_b1', a2: 'formula_a2', b2: 'formula_b2',
    start: 'supplier_contract_start', end: 'supplier_contract_end',
  },
  injection: {
    supplier: 'injection_supplier_name', type: 'injection_formula_type', tariff: 'injection_tariff_type',
    fixed: 'injection_fixed_price', hp: 'injection_fixed_price_hp', hc: 'injection_fixed_price_hc',
    x: 'injection_formula_x_param', a: 'injection_formula_a', b: 'injection_formula_b',
    a1: 'injection_formula_a1', b1: 'injection_formula_b1', a2: 'injection_formula_a2', b2: 'injection_formula_b2',
    start: 'injection_contract_start', end: 'injection_contract_end',
  },
};

/** Consommateur : prélèvement ; producteur : injection ; prosumer : les deux. */
export const contractKinds = (type: string | null | undefined): ContractKind[] =>
  type === 'prosumer' ? ['offtake', 'injection'] : type === 'producer' ? ['injection'] : ['offtake'];

/** Pondération d'un contrat bihoraire pour le graphique : 70 % heures pleines, 30 % heures creuses. */
export const HP_SHARE = 0.7;
export const HC_SHARE = 0.3;

const num = (v: unknown): number | null => (v === null || v === undefined || v === '' ? null : Number(v));
const round = (v: number, d = 4) => Math.round(v * 10 ** d) / 10 ** d;

/** Contrat bihoraire (fixe : prix HP et HC ; variable : formules HP et HC renseignés) ? */
export function isBiHourly(p: any, kind: ContractKind): boolean {
  const F = CONTRACT_FIELDS[kind];
  if (p?.[F.tariff] !== 'bi') return false;
  if (p?.[F.type] === 'variable') return [F.a1, F.b1, F.a2, F.b2].every(col => num(p?.[col]) !== null);
  return num(p?.[F.hp]) !== null && num(p?.[F.hc]) !== null;
}

/** Prix fixe retenu (€/MWh) : monohoraire, ou bihoraire pondéré 70 % HP / 30 % HC. */
export function fixedPriceOf(p: any, kind: ContractKind): number | null {
  const F = CONTRACT_FIELDS[kind];
  if (isBiHourly(p, kind)) return round(HP_SHARE * num(p[F.hp])! + HC_SHARE * num(p[F.hc])!, 2);
  return num(p?.[F.fixed]);
}

/**
 * Formule retenue A × X + B : monohoraire, ou bihoraire pondérée 70 % HP / 30 % HC
 * (0,7 × (A1 X + B1) + 0,3 × (A2 X + B2) = (0,7 A1 + 0,3 A2) X + (0,7 B1 + 0,3 B2)).
 */
export function variableFormulaOf(p: any, kind: ContractKind): { a: number | null; b: number | null } {
  const F = CONTRACT_FIELDS[kind];
  if (isBiHourly(p, kind)) {
    return {
      a: round(HP_SHARE * num(p[F.a1])! + HC_SHARE * num(p[F.a2])!),
      b: round(HP_SHARE * num(p[F.b1])! + HC_SHARE * num(p[F.b2])!),
    };
  }
  return { a: num(p?.[F.a]), b: num(p?.[F.b]) };
}

/** Le site a-t-il un contrat de ce type renseigné ? (un type seul, sans fournisseur ni prix, ne compte pas) */
export function hasSupplierContract(p: any, kind: ContractKind): boolean {
  const F = CONTRACT_FIELDS[kind];
  const v = variableFormulaOf(p, kind);
  return !!(p?.[F.supplier]
    || (p?.[F.type] === 'fixed' && fixedPriceOf(p, kind) !== null)
    || (p?.[F.type] === 'variable' && (v.a !== null || v.b !== null)));
}
